import { Box, Button, Drawer, Grid, Tab, Tabs, Typography } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { Filters, SIXTY_DAYS } from '../DateAndTime.helpers';
import DayFilters from '../DayFilters';

export default function FilterDrawer({
	filters,
	setOpenDrawer,
	handleDrawerClose,
	openDrawer,
	setDate,
	setThreeDaySwitch,
	date,
	threeDaySwitch,
	weekDays,
	timeOfDay,
	setTimeOfDay,
	setWeekDays,
	setFilters,
}: {
	setFilters: ({ timeOfDay, weekDays }: Filters) => void;
	filters: Filters;
	setThreeDaySwitch: (bool: boolean) => void;
	threeDaySwitch: boolean;
	setTimeOfDay: (timeOfDay: string) => void;
	setWeekDays: (weekDays: string[]) => void;
	weekDays: string[];
	timeOfDay: string;
	date: Date;
	setDate: (date: Date) => void;
	openDrawer: boolean;
	setOpenDrawer: (bool: boolean) => void;
	handleDrawerClose: () => void;
}) {
	const today = new Date();
	const [tab, setTab] = useState(0);
	const { t } = useTranslation('dateAndTime');

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTab(newValue);
	};

	return (
		<Drawer open={openDrawer} anchor="bottom" onClose={handleDrawerClose}>
			<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
				<Tabs
					value={tab}
					onChange={handleTabChange}
					centered
					variant="fullWidth"
					sx={{ '.MuiTabs-indicator': { bgcolor: 'secondary.main' } }}
				>
					<Tab
						label={
							<Typography color="secondary.main" variant="bodyMediumBook">
								{t('calendar')}
							</Typography>
						}
					/>
					<Tab
						label={
							<Typography data-test-id="filter-message" color="secondary.main" variant="bodyMediumBook">
								{t('filter')}
							</Typography>
						}
					/>
				</Tabs>
			</Box>
			<CustomTabPanel value={tab} index={0}>
				<StaticDatePicker
					value={date}
					minDate={new Date()}
					maxDate={new Date(today.setDate(today.getDate() + SIXTY_DAYS))}
					shouldDisableDate={(date) => date.getDay() === 0}
					onChange={(newValue) => {
						setDate(newValue ?? new Date());
						setThreeDaySwitch(false);
					}}
					slotProps={{
						toolbar: {
							hidden: true,
						},
						actionBar: {
							actions: [],
						},
					}}
					onAccept={() => setOpenDrawer(false)}
					views={['day']}
				/>
			</CustomTabPanel>
			<CustomTabPanel value={tab} index={1}>
				<DayFilters
					threeDaySwitch={threeDaySwitch}
					timeOfDay={timeOfDay}
					setTimeOfDay={setTimeOfDay}
					weekDays={weekDays}
					setWeekDays={setWeekDays}
				/>
			</CustomTabPanel>
			<Grid container justifyContent="space-between">
				<Grid>
					<Button
						size="M"
						onClick={() => {
							setDate(new Date());
							setTimeOfDay('');
							setWeekDays([]);
						}}
						sx={{ textTransform: 'none' }}
					>
						<Typography color="primary.main" variant="buttonLabel">
							{t('reset')}
						</Typography>
					</Button>
				</Grid>

				<Grid>
					<Button
						size="M"
						onClick={() => {
							setTimeOfDay(filters.timeOfDay ?? '');
							setWeekDays(filters.weekDays ?? []);
							handleDrawerClose();
						}}
						sx={{ textTransform: 'none' }}
					>
						<Typography color="primary.main" variant="buttonLabel">
							{t('cancel')}
						</Typography>
					</Button>
					<Button
						variant="tertiaryDefault"
						size="M"
						onClick={() => {
							setThreeDaySwitch(false);
							setFilters({ weekDays, timeOfDay });
							handleDrawerClose();
						}}
					>
						{t('apply')}
					</Button>
				</Grid>
			</Grid>
		</Drawer>
	);
}

export function CustomTabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}
